// The three bands above the steps — RON-5465.
// Prototype regions: `.visit-banner`, the contractor strip, `.progress-bar-wrap`.
//
// Kept together because they are one block of context: what the visit is, who the pack belongs to,
// and how far through it they are. A contractor who arrived from an email reads these in that
// order before anything below makes sense.

import { useTranslation } from "react-i18next";

import {
  MODE_KIND,
  STEP,
  STEP_LABEL_KEYS,
  formatLongDate,
  formatTime,
  getInitials,
  isStepDone,
} from "../portalConstants.js";

const MetaItem = ({ children, icon }) => (
  <div className="portal-visit__item">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {icon}
    </svg>
    {children}
  </div>
);

const ICONS = {
  date: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </>
  ),
  host: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  purpose: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </>
  ),
  person: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
};

const PortalPackHeader = ({ visit, customer, pack, mode, activeStep }) => {
  const { t } = useTranslation("common");

  const steps = [STEP.READ, STEP.UPLOAD, STEP.PERSONNEL, STEP.SUBMIT];

  // CORRECTED 22-October-2026. This said "THE API SENDS NO VISIT (§3 — the pack stops at
  // `steps`)". That was true when it was written and stopped being true on 09-October, when the
  // visit block was added. It is consumed.
  //
  // `visit` is still `null` when the contractor has no upcoming visit — a normal state, not a
  // failure: a pack belongs to a contractor, not a booking, and a vetted contractor may have
  // nothing in the diary.
  const hasVisit = Boolean(visit?.site || visit?.date || visit?.host || visit?.purpose);

  // ── The mode band — Phase E, AC-P-70 and AC-P-73 ─────────────────────────────────────────
  //
  // A pre-approval or renewal pack says WHO asked and BY WHEN, in place of a visit. Before
  // Phase E the band simply did not draw when there was no visit, which left a contractor with
  // no booking looking at a page that never explained why they had been sent it.
  const isRenewal = mode?.kind === MODE_KIND.RENEWAL;

  return (
    <>
      {(hasVisit || mode?.kind) && (
      <section className="portal-visit">
        <div className="portal-visit__inner">
          {/* ONE BANNER, THREE JOBS — which is how the prototype does it. It swaps this title
              and the meta items below rather than adding a second band; an earlier build here
              rendered a separate `.portal-mode` section, which sat outside the page container
              and read as a detached strip above the header.

              "YOUR NEXT VISIT", not "the visit this pack is for". The Laravel note is explicit:
              "Please word the banner so it does not promise more than that." What comes back is
              the nearest upcoming visit, and a pack can outlive any one of them. */}
          <p className="portal-visit__eyebrow">
            {mode?.kind
              ? t(
                  isRenewal
                    ? "react_portal_mode_renewal_title"
                    : "react_portal_mode_preapproval_title"
                )
              : t("react_portal_visit_eyebrow")}
          </p>
          <p className="portal-visit__name">
            {/* On a pre-approval or renewal pack the customer stands alone: there is no site,
                because there is no visit. */}
            {mode?.kind
              ? customer || ""
              : [customer, visit?.site].filter(Boolean).join(" — ")}
          </p>

          <div className="portal-visit__meta">
            {/* MODE MODE — the prototype replaces all three meta items rather than adding a row:
                calendar, person, clock, in that order. Same icons, same shape, different facts.

                A pre-approval or renewal pack still uses the visit icons because the facts play
                the same roles: when it is, who asked, and by when. */}
            {mode?.kind && !hasVisit && (
              <MetaItem icon={ICONS.date}>{t("react_portal_mode_no_visit")}</MetaItem>
            )}
            {mode?.kind && isRenewal && mode.preapprovedSince && (
              <MetaItem icon={ICONS.date}>
                {t("react_portal_mode_preapproved_since").replace(
                  "%s",
                  formatLongDate(mode.preapprovedSince)
                )}
              </MetaItem>
            )}
            {mode?.kind && mode.requestedBy?.name && (
              <MetaItem icon={ICONS.host}>
                {t(
                  mode.sentAutomatically
                    ? "react_portal_mode_sent_on_behalf"
                    : "react_portal_mode_requested_by"
                ).replace("%s", mode.requestedBy.name)}
              </MetaItem>
            )}
            {mode?.kind && mode.completeBy && (
              <MetaItem icon={ICONS.clock}>
                {t(isRenewal ? "react_portal_mode_renew_by" : "react_portal_mode_complete_by")
                  .replace("%s", formatLongDate(String(mode.completeBy).slice(0, 10)))}
              </MetaItem>
            )}

            {visit?.date && (
              <MetaItem icon={ICONS.date}>
                {visit.endDate && visit.endDate !== visit.date
                  ? `${formatLongDate(visit.date)} – ${formatLongDate(visit.endDate)}`
                  : formatLongDate(visit.date)}
              </MetaItem>
            )}
            {visit?.arrivalTime && (
              <MetaItem icon={ICONS.clock}>
                {t("react_portal_arrival_at").replace("%s", formatTime(visit.arrivalTime))}
              </MetaItem>
            )}
            {visit?.host && (
              <MetaItem icon={ICONS.host}>
                {t("react_portal_invited_by").replace("%s", visit.host)}
              </MetaItem>
            )}
            {visit?.purpose && <MetaItem icon={ICONS.purpose}>{visit.purpose}</MetaItem>}
          </div>
        </div>
      </section>
      )}

      {/* Who the pack belongs to, and who else is coming.
          `contractor` DOES come from the pack, so this band survives without a visit.
          The personnel CHIPS do not: §8 is explicit that step 3 is "a tick, not a crew list — a
          list of names would need its own table and is a separate piece of work". They render
          only where names exist, which today means only on the fixtures. */}
      <section className="portal-who">
        <div className="portal-who__inner">
          <div className="portal-who__person">
            <span className="portal-who__avatar" aria-hidden="true">
              {getInitials(pack?.contractor?.name)}
            </span>
            <div>
              <p className="portal-who__name">{pack?.contractor?.name}</p>
              <p className="portal-who__sub">
                {[pack?.contractor?.trade, pack?.contractor?.company].filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>

          {/* The prototype names the firm separately from the person, and it earns the space: a
              contractor may attend on behalf of a company whose name is the one on the
              insurance certificate uploaded in step 2. */}
          {pack?.contractor?.company && (
            <>
              <span className="portal-who__rule" aria-hidden="true" />
              <div>
                <p className="portal-who__label">{t("react_portal_contracting_company")}</p>
                <p className="portal-who__name">{pack.contractor.company}</p>
              </div>
            </>
          )}

          {Array.isArray(pack?.personnel?.names) && pack.personnel.names.length > 0 && (
            <>
              <span className="portal-who__rule" aria-hidden="true" />
              <div>
                <p className="portal-who__label">{t("react_portal_personnel_attending")}</p>
                <div className="portal-who__chips">
                  {pack.personnel.names.map((person) => (
                    <span className="portal-who__chip" key={person}>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        {ICONS.person}
                      </svg>
                      {person}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* The rail. `done` comes from the server's own per-step counts (see `isStepDone`) rather
          than from counting the rows on screen, so the rail and the cards cannot disagree. */}
      <nav className="portal-progress" aria-label={t("react_portal_progress")}>
        <div className="portal-progress__inner">
          <div className="portal-progress__steps">
            {steps.map((step, index) => {
              const done = isStepDone(pack, step);
              const active = step === activeStep;
              const modifier = done ? " portal-progress__step--done" : active ? " portal-progress__step--active" : "";

              return (
                <div key={step} style={{ display: "contents" }}>
                  <div className={`portal-progress__step${modifier}`}>
                    <span className="portal-progress__circle">
                      {done ? (
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        step
                      )}
                    </span>
                    <span>{t(STEP_LABEL_KEYS[step])}</span>
                  </div>
                  {index < steps.length - 1 && <span className="portal-progress__line" />}
                </div>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
};

export default PortalPackHeader;
